import { isNonEmpty } from '@sgme/fp';
import { createBatchWorkbook } from 'components/Workspaces/Batch/ExportExcel/xlsxBuilder';
import type { SPIntl } from 'contexts/intl/intlContext';
import { type Locale, parseISO } from 'date-fns';
import type { BatchQuoteResult } from 'epic/batchWorkspace/pricingConflationEpics';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import type { FormError } from 'models/formError';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import { AllowedRequests } from 'models/forms/FormResult';
import { formEnrichedDataReceived } from 'state/formData/formData.action';
import type { DataById } from 'state/formData/formData.model';
import { checkFormEnrichmentRequestNeededThunk, resetDefaultDatesThunk } from 'state/formData/formData.thunks';
import { patchedFieldsComputed } from 'state/formData/patchedFields/patchedFields.actions';
import { computePatchedFieldsDiff } from 'state/formData/patchedFields/patchedFields.helper';
import { dispatchCommonFormActionsForWorkspace } from 'state/forms/thunks';
import type { StaticProductView } from 'state/product-description/types';
import { quotesRemoved } from 'state/quotes/quotes.actions';
import { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import {
  areAllBatchFormsSelectedByTabId,
  getBatchFormIdsInTabIsNotError,
  getBatchFormInfos,
  getBatchFormMustForceAdvancedMode,
  getBatchFormQuoteId,
  getBatchFormsFieldMetadata,
  getBatchPayoffType,
  getBatchProductSubType,
  getBatchProductType,
  getBatchQuoteResult,
  getBatchTabIdByFormId,
  getBatchWrapper,
  getFormEnrichedData,
  getFormIdsInBatchTab,
  getFormsEnrichedData,
  getNbOfFormsInBatchTab,
  getSelectedFormIdsInBatchTab,
  isNoFormSelectedByTabId,
} from 'state/selectors';
import { getBatchFieldValuesData } from 'state/selectors/common/batchForm.export';
import type { Thunk } from 'state/state.common';
import { getTabTitle } from 'state/workspaces/tabs/tabs.selector';
import {
  workspaceResetTabCompleted,
  workspaceTabRenamed,
  workspaceTabRepriceRequested,
} from 'state/workspaces/workspace.actions';
import { batchWorkspaceTabInitThunk } from 'state/workspaces/workspace.thunks';
import { downloadExcel } from 'utils/documents/downloadDocument';
import { isHttpError, type HttpClientError } from 'utils/http/httpClient.model';
import { getToastUtils } from 'utils/toast';

import { verifyQuoteImportIdentifiers } from '../../utils/import/importQuote';
import type { QuoteAction } from '../common-form/infos/infos.model';
import type { FieldMetadataById } from '../common-form/metaData/fields/fields.model';
import {
  type BatchFormReloadCorrelation,
  batchFormEnrichmentReceived,
  batchFormReloadFailed,
  batchFormReloadRequested,
  batchFormRollbackRequested,
  batchFormSelectAllToggled,
  batchFormsCloneRequested,
  batchFormsDeleted,
  batchFormsDeleteRequested,
  batchQuoteAborted,
  batchQuoteAddToQueue,
  batchQuoteReceived,
  batchQuoteRemoved,
  batchTradeAuthReceived,
  defaultBatchFormFailed,
  defaultBatchFormReceived,
  defaultBatchFormRequested,
} from './batchForm.action';

const dispatchCommonFormActions = dispatchCommonFormActionsForWorkspace('newBatch');
export function defaultBatchFormRequestedThunk(
  tabId: string,
  productType: string,
  productSubtype: string,
  wrapper: string,
  payoffType?: string,
): Thunk<void> {
  return (dispatch, _, { getNewGuid }) => {
    const temporaryFormId = getNewGuid();
    dispatch(defaultBatchFormRequested(tabId, temporaryFormId, productType, productSubtype, wrapper, payoffType));
  };
}

export function batchFormsReceivedThunk(
  tabId: string,
  temporaryFormIds: readonly string[],
  forms: BatchFormResult[],
): Thunk<void> {
  return (dispatch, _) => {
    forms.forEach((form, index) => {
      if (form) {
        dispatch(
          defaultBatchFormReceivedThunk(
            tabId,
            temporaryFormIds[index],
            form.negotiationId,
            form.productView,
            form.actions,
            form.values,
            form.fieldsMetadata,
            form.quoteQuery,
            form.version,
            false,
            form.allowedRequests,
            form.errors,
          ),
        );
      } else {
        dispatch(defaultBatchFormFailed(temporaryFormIds[index]));
      }
    });
  };
}

// TODO: batch screen unit test
export function defaultBatchFormReceivedThunk(
  tabId: string,
  temporaryFormId: string,
  negotiationId: string,
  productView: StaticProductView,
  actions: QuoteAction[],
  values: DataById,
  fieldsMetadata: FieldMetadataById,
  quoteQuery: unknown,
  version: number,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  reloadCorrelation?: BatchFormReloadCorrelation,
): Thunk<void> {
  return (dispatch, _) => {
    dispatch(
      defaultBatchFormReceived(
        tabId,
        temporaryFormId,
        negotiationId,
        productView,
        actions,
        values,
        fieldsMetadata,
        quoteQuery,
        version,
        mustForceAdvancedMode ?? false,
        validationErrors,
        reloadCorrelation,
      ),
    );
    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests, negotiationId);
  };
}

// TODO: batch screen unit test
export function batchFormEnrichmentReceivedThunk(
  tabId: string,
  formId: string,
  productSubtype: string,
  productView: StaticProductView,
  fieldsMetadata: FieldMetadataById,
  data: DataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const previousEnriched = getFormEnrichedData(state, formId);
    const patched = computePatchedFieldsDiff(previousEnriched, data, fieldsMetadata);
    dispatch(patchedFieldsComputed(formId, patched));

    dispatch(
      batchFormEnrichmentReceived(
        formId,
        productSubtype,
        productView,
        fieldsMetadata,
        version,
        actions,
        quoteQuery,
        mustForceAdvancedMode ?? false,
        validationErrors,
      ),
    );
    dispatchCommonFormActions(dispatch, tabId, data, allowedRequests, formId);
    dispatch(checkFormEnrichmentRequestNeededThunk(tabId, formId, 'newBatch', fieldsMetadata));
  };
}

// TODO: batch screen unit test
export function batchFormSelectAllToggledThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getBatchFormIdsInTabIsNotError(state, tabId);
    const areAllFormsSelected = areAllBatchFormsSelectedByTabId(state, tabId);
    const isNoFormSelected = isNoFormSelectedByTabId(state, tabId);

    let value = false;
    if (areAllFormsSelected) {
      value = false;
    } else if (isNoFormSelected || !areAllFormsSelected) {
      value = true;
    }

    dispatch(batchFormSelectAllToggled(value, formIds));
  };
}

export function batchFormsAllDeletedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getFormIdsInBatchTab(state, tabId);

    if (formIds.length > 0) {
      dispatch(batchQuoteRemovedThunk(tabId, formIds));
      dispatch(batchFormsDeleteRequested(formIds));
      dispatch(batchFormsDeleted(tabId, formIds));
    }
  };
}

// TODO: batch screen unit test
export function selectedBatchFormsDeletedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getSelectedFormIdsInBatchTab(state, tabId);
    dispatch(batchFormsDeletedThunk(tabId, formIds));
  };
}

export function selectedBatchFormToQuoteThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getSelectedFormIdsInBatchTab(state, tabId);
    if (formIds.length > 0) {
      dispatch(batchQuoteAddToQueue(tabId, formIds));
    }
  };
}

export function batchFormsDeletedThunk(tabId: string, formIds: readonly string[]): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const productType = getBatchProductType(state, tabId);
    const productSubtype = getBatchProductSubType(state, tabId);
    const wrapper = getBatchWrapper(state, tabId);
    const payoffType = getBatchPayoffType(state, tabId);
    const totalNbOfForms = getNbOfFormsInBatchTab(state, tabId);

    if (productType && productSubtype && wrapper && totalNbOfForms <= formIds.length) {
      dispatch(defaultBatchFormRequestedThunk(tabId, productType, productSubtype, wrapper, payoffType));
    }

    dispatch(batchQuoteRemovedThunk(tabId, formIds));
    dispatch(batchFormsDeleteRequested(formIds));
    dispatch(batchFormsDeleted(tabId, formIds));
  };
}

export function selectedBatchFormsCloneRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getSelectedFormIdsInBatchTab(state, tabId);
    dispatch(batchFormsCloneRequestedThunk(tabId, formIds));
  };
}

export function selectedBatchFormsOpenInQuoteWorkspaceRequestedThunk(tabId: string): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getSelectedFormIdsInBatchTab(state, tabId);
    dispatch(batchFormsOpenedInQuoteWorkspaceThunk(formIds));
  };
}

export function batchFormsCloneRequestedThunk(tabId: string, formIds: readonly string[]): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();
    const productType = getBatchProductType(state, tabId);
    const productSubType = getBatchProductSubType(state, tabId);
    const wrapper = getBatchWrapper(state, tabId);
    const temporaryFormIds = formIds.map(_ => getNewGuid());
    const formsInfosToBeCloned = getFormsEnrichedData(state, formIds);
    const formsMetadataToBeCloned = getBatchFormsFieldMetadata(state, formIds);
    const formIdsInTab = getFormIdsInBatchTab(state, tabId);
    const mustForceAdvancedMode = getBatchFormMustForceAdvancedMode(state, formIdsInTab);
    if (productType && productSubType && wrapper) {
      dispatch(
        batchFormsCloneRequested(
          tabId,
          formsInfosToBeCloned,
          formsMetadataToBeCloned,
          temporaryFormIds,
          productType,
          productSubType,
          wrapper,
          mustForceAdvancedMode,
        ),
      );
    }
  };
}

export function resetSelectedBatchFormToDefaultDatesThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getSelectedFormIdsInBatchTab(state, tabId);
    formIds.forEach(formId => dispatch(resetDefaultDatesThunk(tabId, formId)));
  };
}

// TODO: batch screen unit test
export function batchFormRestoreSessionThunk(tabId: string, formId: string): Thunk<void> {
  return dispatch => {
    dispatch(batchFormRollbackRequested(tabId, formId));
  };
}

// TODO: batch screen unit test
export function batchQuoteRemovedThunk(tabId: string, formIds: readonly string[]): Thunk<void> {
  return function (dispatch, getState) {
    const state = getState();

    let impactedQuoteIds: readonly string[] = [];
    let impactedFormIds: readonly string[] = [];

    formIds.forEach(formId => {
      const quoteId = getBatchFormQuoteId(state, formId);
      if (quoteId) {
        impactedQuoteIds = [...impactedQuoteIds, quoteId];
        impactedFormIds = [...impactedFormIds, formId];
      }
    });

    if (impactedFormIds.length > 0) {
      dispatch(batchQuoteRemoved(tabId, impactedFormIds));
    }

    if (impactedQuoteIds.length > 0) {
      dispatch(quotesRemoved(undefined, ...impactedQuoteIds));
    }
  };
}

// TODO: batch screen unit test
export function quoteResultReceivedThunk(formId: string, quoteId: string, data: BatchQuoteResult): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const infos = getBatchFormInfos(state, formId);
    const tabId = getBatchTabIdByFormId(state, formId);
    const isQuoteResultKeyExist = getBatchQuoteResult(state, formId) !== undefined;

    const { formResult, ...quoteResult } = data;
    if (formResult !== undefined && infos !== undefined && tabId) {
      const { productView, values, fieldsMetadata, quoteQuery, actions, errors, version, mustForceAdvancedMode } =
        formResult;
      const productSubtype = values['ProductSubtype'] as string;
      const previousEnriched = getFormEnrichedData(state, formId);
      const patched = computePatchedFieldsDiff(previousEnriched, values, fieldsMetadata);
      dispatch(patchedFieldsComputed(formId, patched));
      dispatch(
        batchFormEnrichmentReceived(
          formId,
          productSubtype,
          productView,
          fieldsMetadata,
          version,
          actions,
          quoteQuery,
          mustForceAdvancedMode ?? false,
          errors,
        ),
      );
      dispatch(formEnrichedDataReceived(tabId, formId, values, 'newBatch'));
    }
    if (isQuoteResultKeyExist && tabId) {
      dispatch(batchQuoteReceived(tabId, formId, quoteId, quoteResult));
    }
  };
}

export function batchFormReloadRequestedThunk(tabId: string, identifiers: readonly string[]): Thunk<void> {
  return (dispatch, getState, { getSgmeConfiguration, getNewGuid, getTodayString }) => {
    const toast = getToastUtils(dispatch);
    const identifiersValidation = verifyQuoteImportIdentifiers(identifiers);

    const appState = getState();

    const nbOfForms = getNbOfFormsInBatchTab(appState, tabId);
    const productType = getBatchProductType(appState, tabId);
    const productSubType = getBatchProductSubType(appState, tabId);
    const wrapper = getBatchWrapper(appState, tabId);

    if (!productType || !productSubType || !wrapper) {
      return;
    }

    const maxNbOfForms = getSgmeConfiguration().maxNumberOfFormsPerBatchTab;
    const remainingSlot = maxNbOfForms - nbOfForms;

    if (remainingSlot < identifiersValidation.valid.length) {
      toast.warn(
        {
          id: 'batchPricer.quote.import.maxNbOfFormReached',
          values: {
            nbOfQuotes: identifiersValidation.valid.length,
            remainingSlot,
          },
        },
        {
          id: 'batchPricer.quote.import.maxNbOfFormReached.title',
          values: { maxNbOfForms },
        },
        false,
      );
      return;
    }

    if (identifiersValidation.valid.length > 0) {
      toast.success(
        {
          id: 'batchPricer.quote.import.accepted',
          values: { nbOfQuotes: identifiersValidation.valid.length },
        },
        { id: 'toast.batchPricer' },
      );
    }

    if (identifiersValidation.invalid.length > 0) {
      toast.warn(
        {
          id: 'batchPricer.quote.import.discarded',
          values: { nbOfQuotes: identifiersValidation.invalid.length },
        },
        { id: 'toast.batchPricer' },
        false,
      );
    }

    identifiersValidation.valid.forEach(identifier => {
      const temporaryFormId = getNewGuid();
      dispatch(
        batchFormReloadRequested(
          tabId,
          temporaryFormId,
          identifier,
          IdType.Guess,
          getTodayString(),
          productType,
          productSubType,
          wrapper,
        ),
      );
    });
  };
}

export function batchReloadFailedThunk(
  tabId: string,
  temporaryFormId: string,
  identifier: string,
  error: HttpClientError,
  reloadCorrelation?: BatchFormReloadCorrelation,
): Thunk<void> {
  return dispatch => {
    if (isHttpError(error) && error.status === 406) {
      const toast = getToastUtils(dispatch);
      toast.warn(
        {
          id: 'batchPricer.quote.import.wrong.payoff',
          values: {
            quoteId: identifier,
          },
        },
        { id: 'toast.batchPricer' },
        false,
      );
    }

    dispatch(
      batchFormReloadFailed(temporaryFormId, reloadCorrelation === undefined ? undefined : tabId, reloadCorrelation),
    );

    if (isHttpError(error) && error.status === 406) {
      dispatch(batchFormsDeletedThunk(tabId, [temporaryFormId]));
    }
  };
}

export function batchResetWorkspaceTabThunk(tabId: string): Thunk<void> {
  return (dispatch, _, { getNewGuid }) => {
    dispatch(batchFormsAllDeletedThunk(tabId));
    dispatch(workspaceTabRenamed(tabId, undefined, 'newBatch'));
    const newTabId = getNewGuid();
    dispatch(workspaceResetTabCompleted(tabId, newTabId, 'newBatch'));
    dispatch(batchWorkspaceTabInitThunk(newTabId));
  };
}

export const batchQuoteAbortedThunk =
  (formId: string): Thunk<void> =>
  (dispatch, getState) => {
    const state = getState();
    const quoteId = getBatchFormInfos(state, formId)?.quoteId;
    dispatch(batchQuoteAborted(formId, quoteId));
  };

export const batchQuotesAbortedThunk =
  (tabId: string): Thunk<void> =>
  (dispatch, getState) => {
    const state = getState();
    const formIds = getSelectedFormIdsInBatchTab(state, tabId);
    formIds.forEach(formId => {
      dispatch(batchQuoteAbortedThunk(formId));
    });
  };

export const batchExportExcelThunk =
  (tabId: string, isInternal: boolean, intl: SPIntl, locale: Locale): Thunk<void> =>
  (dispatch, getState) => {
    const toast = getToastUtils(dispatch);
    toast.success(
      {
        id: `batchPricer.quote.export.${isInternal ? 'internal' : 'external'}`,
      },
      { id: 'toast.batchPricer.exportInProgress' },
    );
    const state = getState();
    const fieldValues = getBatchFieldValuesData(state, tabId, intl, locale);
    const workbook = createBatchWorkbook(fieldValues);
    const title = getTabTitle(state, tabId);
    const fileName = `${intl.formatMessage({ id: 'batchPricer.export.title' })} ${
      title?.customizedName ? title?.customizedName : intl.formatMessage({ id: 'batchPricer.export.untitled' })
    }`;
    downloadExcel(workbook, fileName);
  };

export const batchFormsOpenedInQuoteWorkspaceThunk =
  (formIds: readonly string[]): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    let openedQuotesCount = 0;

    formIds.forEach(formId => {
      const form = getBatchFormInfos(state, formId);
      if (form?.quoteId) {
        dispatch(
          workspaceTabRepriceRequested(
            form.quoteId,
            IdType.QuoteId,
            form.quoteId,
            null,
            'static',
            true,
            undefined,
            undefined,
            false,
          ),
        );
        openedQuotesCount++;
      }
    });

    const toast = getToastUtils(dispatch);
    toast.success(
      {
        id: 'openInQuoteWorkspace.toast.content',
        values: { nbOfQuotes: openedQuotesCount },
      },
      { id: 'openInQuoteWorkspace' },
    );
  };

export function batchTradeAuthReceivedThunk(quoteId: string, tradeAuth: NextTradeAuthorizationResult): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const tradeAuthCreatedOn = tradeAuth.createdOn;

    const when =
      tradeAuth.status === 'Granted' && isNonEmpty(tradeAuthCreatedOn) ? parseISO(tradeAuthCreatedOn) : getNow();

    dispatch(batchTradeAuthReceived(quoteId, tradeAuth, when));
  };
}
